'use client';
import React from 'react';
import type { Store } from './storeData';
import Icon from '@/components/ui/AppIcon';

interface StoreDetailPanelProps {
  store: Store;
  onClose: () => void;
}

const statusConfig = {
  open: { label: 'Açık', color: 'text-green-600' },
  'closing-soon': { label: 'Kapanmak Üzere', color: 'text-amber-600' },
  closed: { label: 'Kapalı', color: 'text-red-600' },
};

const weeklyHours = [
  { day: 'Pazartesi', hours: '07:00 – 22:00' },
  { day: 'Salı', hours: '07:00 – 22:00' },
  { day: 'Çarşamba', hours: '07:00 – 22:00' },
  { day: 'Perşembe', hours: '07:00 – 23:00' },
  { day: 'Cuma', hours: '07:00 – 23:00' },
  { day: 'Cumartesi', hours: '08:00 – 23:30' },
  { day: 'Pazar', hours: '09:00 – 22:00' },
];

export default function StoreDetailPanel({ store, onClose }: StoreDetailPanelProps) {
  const status = statusConfig[store.status];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center animate-fade-in">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-app bg-card rounded-t-3xl animate-slide-up max-h-[75vh] overflow-y-auto scrollbar-hide">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>

        <div className="px-5 py-3">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-foreground font-bold text-lg">{store.name}</h2>
              <p className={`text-sm font-semibold ${status.color}`}>{status.label}</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
              <Icon name="XMarkIcon" size={18} className="text-muted-foreground" />
            </button>
          </div>

          {/* Address & phone */}
          <div className="space-y-3 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-accent flex items-center justify-center flex-shrink-0">
                <Icon name="MapPinIcon" size={16} className="text-primary" />
              </div>
              <div>
                <p className="text-foreground text-sm font-medium">{store.address}</p>
                <p className="text-muted-foreground text-xs">{store.distance} uzaklıkta</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-accent flex items-center justify-center flex-shrink-0">
                <Icon name="PhoneIcon" size={16} className="text-primary" />
              </div>
              <a href={`tel:${store.phone}`} className="text-primary text-sm font-medium">
                {store.phone}
              </a>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 mb-4">
            <button className="flex-1 bg-primary text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors active:scale-[0.98]">
              <Icon name="MapIcon" size={16} className="text-white" />
              <span className="text-sm">Yol Tarifi</span>
            </button>
            <a
              href={`tel:${store.phone}`}
              className="flex-1 bg-muted text-foreground font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-muted/80 transition-colors"
            >
              <Icon name="PhoneIcon" size={16} className="text-foreground" />
              <span className="text-sm">Ara</span>
            </a>
          </div>

          {/* Amenities */}
          <div className="mb-4">
            <p className="text-foreground font-semibold text-sm mb-2">Olanaklar</p>
            <div className="flex flex-wrap gap-2">
              {store.amenities.map((a) => (
                <span key={`amenity-${store.id}-${a}`} className="bg-accent text-foreground text-xs font-medium px-3 py-1.5 rounded-full">
                  {a}
                </span>
              ))}
            </div>
          </div>

          {/* Weekly hours */}
          <div>
            <p className="text-foreground font-semibold text-sm mb-2">Çalışma Saatleri</p>
            <div className="bg-muted rounded-xl overflow-hidden">
              {weeklyHours.map((wh, i) => {
                const isToday = i === 3; // Thursday mock
                return (
                  <div
                    key={`hours-${store.id}-${wh.day}`}
                    className={`flex items-center justify-between px-3 py-2 ${i < weeklyHours.length - 1 ? 'border-b border-border' : ''} ${isToday ? 'bg-accent' : ''}`}
                  >
                    <span className={`text-sm ${isToday ? 'text-primary font-bold' : 'text-foreground font-medium'}`}>
                      {wh.day} {isToday && '(Bugün)'}
                    </span>
                    <span className={`text-sm font-tabular ${isToday ? 'text-primary font-bold' : 'text-muted-foreground'}`}>
                      {wh.hours}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}